import type { Session } from '@supabase/supabase-js';
import type { AppState } from './types';
import { rebaseValue } from './rebase';

const ACCOUNT_TIMEOUT_MS = 20_000;

function bounded<T>(operation: Promise<T>, controller: AbortController): Promise<T> {
  const signal = controller.signal;
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      callback();
    };
    const aborted = () => finish(() => reject(new Error('Account load cancelled.')));
    const timer = setTimeout(() => {
      finish(() => reject(new Error('Your account took too long to load. Please retry.')));
      controller.abort();
    }, ACCOUNT_TIMEOUT_MS);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) aborted();
    operation.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error)),
    );
  });
}

/** The bootstrap omits paginated collections; refreshing it must retain hydrated pages. */
export function mergeAccountRefresh(
  before: AppState,
  fresh: AppState,
  current: AppState,
): AppState {
  if (before.current_user_id !== fresh.current_user_id) return fresh;
  const members = new Map(before.members.map((member) => [member.id, member]));
  for (const member of fresh.members) members.set(member.id, member);
  // Pager products include richer variants, offers and images than bootstrap products.
  const products = new Map(fresh.products.map((product) => [product.id, product]));
  for (const product of before.products) products.set(product.id, product);
  const next: AppState = {
    ...fresh,
    members: [...members.values()],
    products: [...products.values()],
    reviews: before.reviews,
    posts: before.posts,
    topics: before.topics,
    listings: before.listings,
    conversations: before.conversations,
    audit_events: before.audit_events,
    follows: [
      ...before.follows.filter(
        (follow) =>
          follow.follower_id !== fresh.current_user_id &&
          follow.following_id !== fresh.current_user_id,
      ),
      ...fresh.follows,
    ],
  };
  // Saves and narrow avatar refreshes can finish during the account read.
  return rebaseValue(before, current, next) as AppState;
}

interface AccountLifecycleOptions {
  getSession: () => Promise<Session | null>;
  load: (userId: string, signal: AbortSignal) => Promise<AppState>;
  getState: () => AppState;
  empty: (userId?: string) => AppState;
  commit: (state: AppState) => void;
  session: (session: Session | null) => void;
  ready: (ready: boolean) => void;
  loading: (loading: boolean) => void;
  error: (error: string | null) => void;
}

/** Auth callbacks only accept identity; Supabase reads run outside the auth lock. */
export function createAccountLifecycle(options: AccountLifecycleOptions) {
  let identity: string | null | undefined;
  let accessToken: string | undefined;
  let version = 0;
  let disposed = false;
  let ready = false;
  let scheduled: ReturnType<typeof setTimeout> | undefined;
  let sessionRead: Promise<void> | undefined;
  let sessionAbort: AbortController | undefined;
  let active: { version: number; abort: AbortController; promise: Promise<void> } | undefined;
  const isCurrent = (userId: string, expectedVersion: number) =>
    !disposed && identity === userId && version === expectedVersion;

  function scheduleRefresh() {
    if (scheduled !== undefined) clearTimeout(scheduled);
    // Calling Supabase inside its auth callback can deadlock subsequent requests.
    scheduled = setTimeout(() => {
      scheduled = undefined;
      void refresh();
    }, 0);
  }

  function acceptSession(next: Session | null, verifyAuthorization = false) {
    if (disposed) return;
    const userId = next?.user.id ?? null;
    const tokenChanged = accessToken !== next?.access_token;
    accessToken = next?.access_token;
    options.session(next);
    if (identity === userId) {
      if (userId && tokenChanged && verifyAuthorization) {
        active?.abort.abort();
        active = undefined;
        scheduleRefresh();
      }
      return;
    }
    identity = userId;
    version += 1;
    ready = false;
    if (scheduled !== undefined) clearTimeout(scheduled);
    active?.abort.abort();
    active = undefined;
    options.commit(options.empty(userId ?? ''));
    options.ready(!userId);
    options.error(null);
    options.loading(Boolean(userId));
    if (userId) scheduleRefresh();
  }

  async function bootstrap(): Promise<void> {
    if (disposed) return;
    if (sessionRead) return sessionRead;
    if (identity === undefined) {
      options.error(null);
      options.loading(true);
    }
    const expectedVersion = version;
    const abort = new AbortController();
    sessionAbort = abort;
    const request = (async () => {
      try {
        const next = await bounded(options.getSession(), abort);
        if (!disposed && version === expectedVersion) acceptSession(next);
      } catch (error) {
        if (!disposed && version === expectedVersion) {
          options.error(error instanceof Error ? error.message : 'Could not load your account.');
          options.loading(false);
        }
      }
    })();
    sessionRead = request;
    await request;
    if (sessionRead === request) sessionRead = undefined;
  }

  async function refresh(): Promise<void> {
    if (disposed) return;
    if (identity === undefined) return bootstrap();
    if (!identity) return;
    if (active?.version === version) return active.promise;
    if (scheduled !== undefined) {
      clearTimeout(scheduled);
      scheduled = undefined;
    }
    const userId = identity;
    const expectedVersion = version;
    const before = options.getState();
    const hadSnapshot = ready;
    const abort = new AbortController();
    options.error(null);
    if (!hadSnapshot) options.loading(true);
    const request = Promise.resolve().then(async () => {
      try {
        if (!isCurrent(userId, expectedVersion) || abort.signal.aborted) return;
        const next = await bounded(options.load(userId, abort.signal), abort);
        if (!isCurrent(userId, expectedVersion) || abort.signal.aborted) return;
        // Narrow realtime reads can complete during initial bootstrap too.
        options.commit(mergeAccountRefresh(before, next, options.getState()));
        ready = true;
        options.ready(true);
      } catch (error) {
        if (isCurrent(userId, expectedVersion) && active?.promise === request)
          options.error(error instanceof Error ? error.message : 'Could not load your account.');
      } finally {
        // Promise.all can reject before sibling reads settle; stop their network work too.
        abort.abort();
        if (active?.promise === request) {
          if (isCurrent(userId, expectedVersion)) options.loading(false);
          active = undefined;
        }
      }
    });
    active = { version: expectedVersion, abort, promise: request };
    return request;
  }

  return {
    acceptSession,
    bootstrap,
    refresh,
    version: () => version,
    isCurrent,
    dispose() {
      disposed = true;
      version += 1;
      if (scheduled !== undefined) clearTimeout(scheduled);
      active?.abort.abort();
      sessionAbort?.abort();
    },
  };
}
